import type { Bid } from '@/types/bid'
import type { BidForm } from '@/types/task'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { bidTask, getBids, selectBid } from '@/api/task'
import { deleteBid } from '@/api/task/bid'

export const useBidStore = defineStore('bid-v2', () => {
  const loading = ref(false)
  const bids = ref<Bid[]>([])
  const currentBid = ref<Bid | null>(null)

  // Getters
  const activeBids = computed(() => bids.value.filter(b => b.status === 'pending'))
  const acceptedBid = computed(() => bids.value.find(b => b.status === 'selected'))

  // Actions
  const fetchBids = async (taskId: string | number) => {
    loading.value = true
    try {
      bids.value = await getBids(taskId)
    }
    finally {
      loading.value = false
    }
  }

  const placeBid = async (taskId: string | number, data: BidForm) => {
    loading.value = true
    try {
      await bidTask(taskId, data)
      await fetchBids(taskId)
    }
    finally {
      loading.value = false
    }
  }

  const acceptBidAction = async (bidId: string | number, taskId: string | number) => {
    loading.value = true
    try {
      await selectBid(taskId, bidId)
      await fetchBids(taskId)
    }
    finally {
      loading.value = false
    }
  }

  const withdrawBidAction = async (bidId: string | number, taskId: string | number) => {
    loading.value = true
    try {
      await deleteBid(bidId)
      await fetchBids(taskId)
    }
    finally {
      loading.value = false
    }
  }

  return {
    loading,
    bids,
    currentBid,
    activeBids,
    acceptedBid,
    fetchBids,
    placeBid,
    acceptBid: acceptBidAction,
    withdrawBid: withdrawBidAction,
  }
})
